import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flag as FlagIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ReportErrorDialog({ speechId }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!message.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/reports", {
        speech_id: speechId,
        message,
        email: email || null,
      });
      toast.success(data.message);
      setMessage("");
      setEmail("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="report-error-btn" className="rounded-sm gap-1.5">
          <FlagIcon className="h-4 w-4" /> Segnala un errore
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-serif">Segnala un errore nella traduzione</DialogTitle>
          <DialogDescription>
            Indica il passaggio da verificare e spiegaci cosa non torna. Controlleremo la
            segnalazione e correggeremo il testo, se necessario.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="report-msg">Che cosa dobbiamo verificare? *</Label>
            <Textarea
              id="report-msg"
              data-testid="report-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Indica il minuto o il paragrafo e descrivi l'errore…"
              className="rounded-sm mt-1.5 min-h-28"
            />
          </div>
          <div>
            <Label htmlFor="report-email">Email per ricevere un riscontro (facoltativa)</Label>
            <Input
              id="report-email"
              type="email"
              data-testid="report-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Per ricevere un riscontro"
              className="rounded-sm mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              L'indirizzo email è facoltativo e sarà utilizzato esclusivamente per rispondere alla
              segnalazione. Consulta la <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="report-close-btn">
            Chiudi
          </Button>
          <Button onClick={submit} disabled={loading} data-testid="report-submit-btn" className="rounded-sm">
            Invia la segnalazione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
